import { UUID } from '@Protium/vue-ozone-utils/types/utils/uuid'
import { io, Socket, SocketOptions, ManagerOptions } from 'socket.io-client'

interface ListenerMap {
  [key: string]: any;
}

export default class IO {
  private client: Socket | undefined
  private host: string
  private namespace: string
  private _listeners: ListenerMap
  private options: ManagerOptions & SocketOptions

  private _user?: UUID
  private _connect_count: number

  constructor (host: string, options?: Partial<ManagerOptions & SocketOptions>, user?: UUID) {
    this.client = undefined
    this.host = host
    this.namespace = '/'
    this._listeners = {}
    this._connect_count = 0

    this._user = user

    this.options = {
      ...(options || {} as any),
      host,
      withCredentials:
        options?.withCredentials !== undefined ? options.withCredentials : true,
      path: options?.path || '/socket.io',
    }
  }

  get user () {
    return this._user
  }

  get reconnected () {
    return this._connect_count > 1
  }

  get listeners () {
    return this._listeners
  }
  set listeners ({ key, value }) {
    this._listeners[key] = value
  }

  get connectCount () {
    return this._connect_count || 0
  }
  get connected () {
    return this.client?.connected
  }

  onConnect (callback: (wasReconnected: boolean) => void) {
    if (!this.client) return
    this.client.off('connect', () => callback(this.reconnected))
    return this.client.on('connect', () => callback(this.reconnected))
  }
  onDisconnect (callback: (reason: Socket.DisconnectReason) => void) {
    if (!this.client) return
    this.client.off('disconnect', callback)
    return this.client.on('disconnect', callback)
  }

  init (namespace = this.namespace, host?: string, subspace = 'service') {
    this.namespace = `${namespace}`
    if (host) this.host = this.options.host = host
    this.client = io(`${this.host}${this.namespace}`, this.options)
    if (process.env.NODE_ENV === 'development') console.log(`--- Socket.IO client initialized --- \nUser      | ${this.user}\nHost      | ${this.host}\nNamespace | ${this.namespace}`)

    this.onConnect(() => {
      this._connect_count++
      console.log(`Client connected | ${this._user} | ${this.host} | [${this.namespace}]`)
      if (this.user) this.join('user', this.user)
    })
    this.onDisconnect((reason) => {
      console.log(
        `Client disconnected | ${this._user} | ${this.host} | [${this.namespace}]`,
        reason
      )
    })

    // this.client.onAny((message, data) => {
    //   console.debug('message received', message, data)
    // })
  }

  setHost (host: string, reinitialize = true) {
    this.host = host
    if (reinitialize)
      this.client = io(`${this.host}${this.namespace}`, this.options)
    // console.log('- Host set - ', `${this.host}${this.namespace}`)
  }

  listen (event: string, callback: (payload: any) => any) {
    if (!this.client)
      throw Error(
        'IO.Listen was called, but client is not defined. call IO.setHost so set the client host first'
      )
    this._listeners[event] = this.client.on(event, callback)

    // console.log('Listener registered', event)
    return this._listeners[event]
  }

  speak (type: string, message: any) {
    this.client?.emit(type, message)
  }

  join (room: string, user: string) {
    if (!this.client) throw Error('No client to make request')
    if (!room) throw Error('Provide a room to join')
    this.speak(`join/${room}`, { user })
    if (process.env.NODE_ENV === 'development') console.log(`Requested to join room "${room}"`)

    if (!this._listeners['join/accept']) {
      this._listeners['join/accept'] = this.client?.on('join/accept', (payload) => console.log(`Join request for room ${payload?.room} accepted`))
    }
  }

  leave (room: string, user: string) {
    if (!this.client) throw Error('No client to make request')
    if (!room) throw Error('Provide a room to leave')
    this.speak(`leave/${room}`, { user })
    if (process.env.NODE_ENV === 'development') console.log(`Requested to leave room "${room}"`)

    if (!this._listeners['leave/accept']) {
      this._listeners['leave/accept'] = this.client?.on('leave/accept', (payload) => console.log(`leave request for room ${payload?.room} accepted`))
    }
  }
}
